import "./side.css";
import { EducatorSide } from "./pages/Educator/EducatorSide";
import { colors, constants } from "utils";
import { Header } from "./Header";
import { pageTitleInsetStyle } from "./pageTitleInset";
import { Button } from "react-bootstrap";
import React, { useState, useEffect } from "react";
import { faBars, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AdminSide } from "./pages/Admin/AdminSide";
import { request } from "hooks/useOkoApi/useOkoApi";
import { auth } from "hooks/useFirebase";
import { useHistory } from "react-router-dom";
import { TeacherModeBanner } from "./pages/Educator/TeacherModeBanner";
import { useImpersonation } from "hooks/useImpersonation/useImpersonation";
import { useAuthUserDataContext } from "hooks/useAuthUserData/useAuthUserData";
import { clearAllFilters } from "utils/persistFilters";
import { LiveSessionsProvider } from "hooks/useLiveSessionsStream/LiveSessionsProvider";

export const SidebarView = ({ sideVariant, viewComponent, headerTitle }) => {
  const { impersonatedUser, stopImpersonating } = useImpersonation();
  const isTeacherMode = sideVariant === "educator" && !!impersonatedUser;
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [adminAccessChecked, setAdminAccessChecked] = useState(false);
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [adminUserData, setAdminUserData] = useState(null);
  const history = useHistory();
  const { authUserData } = useAuthUserDataContext() || {};

  // Every teacher page passes through here, so this is the one place a
  // logged-out teacher is sent to the login page. authUserData is null while
  // auth is still loading or after a failed profile read, so the check waits.
  useEffect(() => {
    if (sideVariant === "educator" && authUserData?.isAnonymous) {
      history.push(constants.EDUCATOR_LOGIN_PATH);
    }
  }, [sideVariant, authUserData?.isAnonymous, history]);

  useEffect(() => {
    if (sideVariant === "admin" && !adminAccessChecked) {
      request("admin/me", { method: "GET" })
        .then((userData) => {
          setHasAdminAccess(true);
          setAdminUserData(userData);
          setAdminAccessChecked(true);
        })
        .catch((err) => {
          const notLoggedIn = !auth.currentUser || auth.currentUser.isAnonymous || err?.status === 401;
          if (notLoggedIn) {
            const requestedPage = window.location.pathname + window.location.search;
            history.push(`${constants.ADMIN_LOGIN_PATH}?next=${encodeURIComponent(requestedPage)}`);
          } else if (err?.status === 403) {
            history.push(constants.EDUCATOR_PATH);
          }
          setAdminAccessChecked(true);
        });
    }
  }, [sideVariant, adminAccessChecked, history]);
  const openSidebar = () => {
    document.getElementById("sidebar-toggle").style.display = "flex";
    document.getElementById("main-view").style.marginLeft = "312px";
  }

  const closeSidebar = () => {
    document.getElementById("sidebar-toggle").style.display = "none";
    document.getElementById("main-view").style.marginLeft = 0;
  }

  const toggleSidebar = () => {
    if (isSidebarOpen) {
      closeSidebar();
    } else {
      openSidebar();
    }
    setIsSidebarOpen(!isSidebarOpen);
  };

  // For admin routes, show loading until access is verified
  if (sideVariant === "admin" && !adminAccessChecked) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
      </div>
    );
  }

  let sideComponent;

  if (sideVariant === "educator") {
    sideComponent = <EducatorSide showCloseButton={isSidebarOpen} toggleSidebar={toggleSidebar} />;
  } else if (sideVariant === "admin") {
    sideComponent = <AdminSide showCloseButton={isSidebarOpen} toggleSidebar={toggleSidebar} adminUserData={adminUserData} />;
  }

  // The educator chrome owns the live-session stream: the side nav's Live
  // Sessions item, the Live Sessions page and Session History share the one
  // connection.
  const Shell = sideVariant === "educator" ? LiveSessionsProvider : React.Fragment;

  return (
    <Shell>
    <div>
      <a href="#main-view" className="skip-to-main">
        Skip to main content
      </a>
      {sideComponent}
      <main
        id="main-view"
        style={{ marginLeft: "312px", transition: "margin-left .05s", ...pageTitleInsetStyle(isSidebarOpen) }}
      >
        <Header title={headerTitle}/>
        {isTeacherMode ? (
          <>
            <TeacherModeBanner
              teacher={impersonatedUser}
              onStop={() => {
                stopImpersonating();
                clearAllFilters();
                window.location.assign(constants.EDUCATOR_PATH);
              }}
            />
            <div style={{ border: `4px solid ${colors.pink800}` }}>
              {viewComponent}
            </div>
          </>
        ) : sideVariant === "admin" && adminUserData ? (
          React.cloneElement(viewComponent, { adminUserData })
        ) : (
          viewComponent
        )}
      </main>
      {!isSidebarOpen &&
        <Button id="sidebar-toggle"
                aria-label="Open sidebar"
                onClick={toggleSidebar}
                style={{
                  backgroundColor: colors.nightlyWoods500,
                  border: "none",
                  position: "absolute",
                  left: "8px",
                  top: "28px" }}>
          <FontAwesomeIcon icon={faBars} />
        </Button>}
    </div>
    </Shell>
  );
};
